import { isMobileApp } from '@cvs-web/splash-components/server';
import { ERROR_PAGE_URL, LOGIN_URL, URL_PREFIX } from '../constants';
import { environment } from '../utils/utils';
import { isHealth100Client } from '../utils/utils';
import {
  GetCommunicationPreferencesResponse,
  GetRxCommunicationPreferencesResponse,
  UpdateCommunicationPreferences,
  RxPreference,
  UpdateCommPrefsResponse,
  ECUserData,
  GetXLoBCommPrefsResponse,
  TargetIdType,
  UpdateXLoBCommPrefsPayload,
  UpdateExtraCareProfile,
  ExperienceResponse,
  H100UpdateCommPrefsPayload,
} from '../types/datatypes';

export class CVSData {
  static readonly doFetch = async <T>(url: string, options: RequestInit): Promise<T> => {
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');
    if (isHealth100Client()) {
      headers.set('x-app-origin', 'h100');
    }
    const res = await fetch(url, { ...options, headers });
    const response = res.json();
    if (!res.ok) {
      if (res.status === 401) {
        if (isMobileApp() && isHealth100Client()) {
          window.location.href = `${URL_PREFIX}${ERROR_PAGE_URL}`;
        } else {
          window.location.href = `${LOGIN_URL}?returnUrl=${encodeURI(window.location.pathname)}`;
        }
      }
      throw Error('An unknown server error occurred');
    }
    return response;
  };

  // TODO remove when old comm-prefs page is retired
  static readonly getCommPrefs = async (): Promise<GetCommunicationPreferencesResponse> => {
    const experienceId = isHealth100Client()
      ? environment.getH100CommPrefsExperienceId
      : environment.getCommPrefsExperienceId;
    const url = `/mcapi/client/experience/v2/load/${experienceId}`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'x-clientId': environment.experienceClientId,
        'x-experienceId': experienceId,
        'x-api-key': environment.experienceApiQee,
        'x-route': 'account-gke-rke',
        // TODO needed for consents resolver, hopefully we can remove in the future
        'x-ice-split': 'Y',
      },
      body: JSON.stringify({
        data: {
          idType: 'RETAIL_PROFILE_ID_TYPE',
        },
      }),
    };

    return this.doFetch<GetCommunicationPreferencesResponse>(url, requestOptions);
  };

  static readonly getRxCommPrefs = async (): Promise<GetRxCommunicationPreferencesResponse> => {
    const experienceId = isHealth100Client()
      ? environment.getH100RxCommPrefsExperienceId
      : environment.getRxCommPrefsExperienceId;
    const url = `/mcapi/client/experience/v2/load/${experienceId}`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'x-clientId': environment.experienceClientId,
        'x-experienceId': experienceId,
        'x-api-key': environment.experienceApiQee,
        'x-route': 'account-gke-rke',
      },
      body: JSON.stringify({
        data: {
          idType: 'RETAIL_PROFILE_ID_TYPE',
          preferenceType: ['PHARMACY_PREFERENCES'],
        },
      }),
    };

    return this.doFetch<GetRxCommunicationPreferencesResponse>(url, requestOptions);
  };

  static getXLoBCommPrefs = async (targetIdTypes: TargetIdType[]): Promise<GetXLoBCommPrefsResponse> => {
    const experienceId = isHealth100Client()
      ? environment.getH100XLoBCommPrefsExpId
      : environment.getXLoBCommPrefsExpId;
    const url = `/mcapi/client/experience/v2/load/${experienceId}`;
    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        'x-clientId': environment.experienceClientId,
        'x-experienceId': experienceId,
        'x-api-key': environment.experienceApiQee,
        'x-route': 'account-gke-rke',
      },
      body: JSON.stringify({
        data: {
          idType: 'RETAIL_PROFILE_ID_TYPE',
          targetIdTypes,
        },
      }),
    };

    return this.doFetch(url, requestOptions);
  };

  static readonly updateH100CommPrefs = async (options: H100UpdateCommPrefsPayload) => {
    const experienceId = environment.updateH100CommPrefsExperienceId;
    const url = `/mcapi/client/experience/v2/load/${experienceId}`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'x-clientId': environment.experienceClientId,
        'x-experienceId': experienceId,
        'x-api-key': environment.experienceApiQee,
        'x-route': 'account-gke-rke',
        'x-source': 'CVSDOTCOM_WEB',
      },
      body: JSON.stringify(options),
    };

    return this.doFetch(url, requestOptions);
  };

  private static readonly updateCommPrefsExp = async (options: UpdateCommunicationPreferences) => {
    const experienceId = isHealth100Client()
      ? environment.updateH100CommPrefsExperienceId
      : environment.updateCommPrefsExperienceId;
    const url = `/mcapi/client/experience/v2/load/${experienceId}`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'x-clientId': environment.experienceClientId,
        'x-experienceId': experienceId,
        'x-api-key': environment.experienceApiQee,
        'x-route': 'account-gke-rke',
        'x-source': 'CVSDOTCOM_WEB',
      },
      body: JSON.stringify({
        data: {
          idType: 'RETAIL_PROFILE_ID_TYPE',
          preferences: [
            {
              preferenceType: 'COMMUNICATION_PREFERENCE',
              programs: [
                {
                  name: 'CVS',
                  channels: [
                    {
                      system: 'EMAIL',
                      subscriptionStatus: options.preferences.cvsComEmail ? 'Y' : 'N',
                    },
                  ],
                },
                // TODO remove after F204530 goes live
                ...(!Object.hasOwn(options.preferences, 'minuteClinicEmail')
                  ? []
                  : [
                      {
                        name: 'MC',
                        channels: [
                          {
                            system: 'EMAIL',
                            subscriptionStatus: options.preferences.minuteClinicEmail ? 'Y' : 'N',
                          },
                        ],
                      },
                    ]),
              ],
            },
          ],
        },
      }),
    };

    return this.doFetch(url, requestOptions);
  };

  static readonly updateCommPrefs = async (options: UpdateCommunicationPreferences) => {
    return Promise.all([this.updateCommPrefsExp(options)]);
  };

  static updateXLoBCommPrefs = async (options: UpdateXLoBCommPrefsPayload): Promise<UpdateCommPrefsResponse> => {
    const experienceId = isHealth100Client()
      ? environment.updateH100CommPrefsExperienceId
      : environment.updateCommPrefsExperienceId;
    const url = `/mcapi/client/experience/v2/load/${experienceId}`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'x-clientId': environment.experienceClientId,
        'x-experienceId': experienceId,
        'x-api-key': environment.experienceApiQee,
        'x-route': 'account-gke-rke',
        'x-source': 'CVSDOTCOM_WEB',
      },
      body: JSON.stringify(options),
    };

    return this.doFetch(url, requestOptions);
  };

  static updatePharmacyCommPrefs = async (preferences: RxPreference[]): Promise<UpdateCommPrefsResponse> => {
    const headerKeys = isHealth100Client()
      ? environment.updateH100PharmacyHeaderKeys
      : environment.updatePharmacyHeaderKeys;
    const url = `/mcapi/client/experience/v2/load/${headerKeys.experienceId}`;
    const requestOptions: RequestInit = {
      method: 'POST',
      body: JSON.stringify({
        data: {
          idType: 'RETAIL_PROFILE_ID_TYPE',
          preferences: preferences,
        },
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-api-key': environment.experienceApiQee,
        'x-clientId': headerKeys.clientId,
        'x-experienceId': headerKeys.experienceId,
        'x-route': 'account-gke-rke',
      },
    };
    return this.doFetch(url, requestOptions);
  };

  static getExtracareInfo = async (
    ecCardNumber: string,
    ecCardType: string,
    tableNames?: string[],
  ): Promise<ECUserData> => {
    const url = `/mcapi/client/experience/v2/load/${environment.getExtracareInfoHeaderKeys.experienceId}`;
    const encryptedECCardNumber = btoa(ecCardNumber);
    const requestOptions: RequestInit = {
      method: 'POST',
      body: JSON.stringify({
        data: {
          idType: 'RETAIL_PROFILE_ID_TYPE',
          getCustomerProfileRequest: {
            xtraCarePrefs: [],
            cardType: ecCardType,
            xtraCare: [],
            extraCareCard: encryptedECCardNumber,
            tables: tableNames
              ? tableNames.map((tableType) => {
                  return { tableName: tableType };
                })
              : [{ tableName: 'customer_email' }],
          },
        },
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-route': 'account-gke-rke',
        'x-api-key': environment.experienceApiQee,
        'x-clientId': environment.getExtracareInfoHeaderKeys.clientId,
        'x-experienceId': environment.getExtracareInfoHeaderKeys.experienceId,
      },
    };
    return this.doFetch(url, requestOptions);
  };

  static updateExtraCareProfile = async (options: UpdateExtraCareProfile): Promise<ExperienceResponse> => {
    const url = `/mcapi/client/experience/v2/load/${environment.updateExtraCareProfileInfo}`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'x-clientId': environment.experienceClientId,
        'x-experienceId': environment.updateExtraCareProfileInfo,
        'x-api-key': environment.experienceApiQee,
        'x-route': 'account-gke-rke',
        'x-source': 'CVSDOTCOM_WEB',
      },
      body: JSON.stringify({
        data: {
          request: options,
        },
      }),
    };

    return this.doFetch(url, requestOptions);
  };
}
